import { __rest } from 'tslib';
import { jsx, Fragment } from 'react/jsx-runtime';
import React__default, { useCallback, useMemo } from 'react';
import ReactSelect, { createFilter } from 'react-select';
import MuiTypography from '@mui/material/Typography';
import DropdownIndicator from './components/DropdownIndicator.js';
import Control from './components/Control.js';
import { WarningLevel } from './types.js';
import useGlobalStyles from '../../theme/global.js';
import useSelectStyles from './hooks/useSelectStyles.js';
import { hasInvalidValue, findMissingOptions } from './utils.js';

const BaseDropdown = (_a, ref) => {
    var { components, startComponent, noOptionsMessage = 'No results found...', isDisabled, isMulti, isOrdered, error, modifiedManually, inactive, size, styles, isOpen, withBorder, menuIsOpen, portalElemId, onChange, value, options, allOptions = options, onFocus, shouldCloseMenuOnSelect = true, invalidItemHint = 'This item is no longer available', invalidItemWarningLevel = WarningLevel.Error } = _a, rest = __rest(_a, ["components", "startComponent", "noOptionsMessage", "isDisabled", "isMulti", "isOrdered", "error", "modifiedManually", "inactive", "size", "styles", "isOpen", "withBorder", "menuIsOpen", "portalElemId", "onChange", "value", "options", "allOptions", "onFocus", "shouldCloseMenuOnSelect", "invalidItemHint", "invalidItemWarningLevel"]);
    useGlobalStyles();
    // in case of a multi line drop down (ordered drop down) we handle the case of invalid items
    // differently since the way we display the selected value and options (all options not only filtered by search) is different
    const containsInvalidValue = !isOrdered && hasInvalidValue(allOptions, value);
    const missingOptions = findMissingOptions(allOptions, value);
    let defaultValue;
    const missingValue = (label) => {
        return (jsx(Fragment, { children: jsx("span", Object.assign({ className: "invalidItem", style: { textDecoration: 'line-through', fontSize: '12px' } }, { children: jsx(MuiTypography, { children: label !== null && label !== void 0 ? label : 'Invalid item' }) })) }));
    };
    if (containsInvalidValue) {
        options = [...options];
        const createMissingValue = (missingOption) => (Object.assign(Object.assign({}, missingOption), { hint: invalidItemHint, hintDuration: 'fast', component: ({ label }) => missingValue(label) }));
        if (!isMulti) {
            options.unshift(Object.assign(Object.assign({}, value), { hint: invalidItemHint, hintDuration: 'fast', component: ({ label }) => missingValue(label) }));
        }
        else if (missingOptions) {
            const mappedMissingOptions = missingOptions.map(createMissingValue);
            options.unshift(...mappedMissingOptions);
        }
    }
    const noOptionsMessageInternal = useCallback((data) => {
        if (typeof noOptionsMessage === 'function') {
            return noOptionsMessage(data);
        }
        return noOptionsMessage || null;
    }, [noOptionsMessage]);
    const baseComponents = useMemo(() => {
        if (startComponent) {
            return Object.assign({ DropdownIndicator, Control: (_a) => {
                    var { selectProps } = _a, rest = __rest(_a, ["selectProps"]);
                    return (jsx(Control, Object.assign({}, rest, { selectProps: Object.assign(Object.assign({}, selectProps), { startComponent }) })));
                } }, components);
        }
        return Object.assign({ DropdownIndicator,
            Control }, components);
    }, [components, startComponent]);
    const configStyles = useSelectStyles({
        error: error || (invalidItemWarningLevel === WarningLevel.Error && containsInvalidValue),
        size,
        isOpen: menuIsOpen || isOpen,
        withBorder,
        modifiedManually,
        inactive,
        isDisabled: isDisabled,
        containsInvalidValue,
        isMultiLine: isOrdered,
        warning: !error && invalidItemWarningLevel === WarningLevel.Warning && containsInvalidValue,
    }, styles);
    return (jsx(ReactSelect, Object.assign({ noOptionsMessage: noOptionsMessageInternal, isOptionDisabled: option => 'disabled' in option && !!option.disabled, isDisabled: isDisabled, isMulti: isMulti, components: baseComponents, menuPortalTarget: portalElemId ? document.getElementById(portalElemId) : document.body, menuPlacement: "auto", onChange: onChange, options: options, value: value }, (defaultValue ), { onFocus: onFocus }, rest, { ref: ref, menuIsOpen: menuIsOpen, openMenuOnClick: !isDisabled, openMenuOnFocus: !isDisabled, isSearchable: !isMulti, blurInputOnSelect: !isMulti, closeMenuOnSelect: !isMulti && shouldCloseMenuOnSelect, isClearable: false, backspaceRemovesValue: false, hideSelectedOptions: false, captureMenuScroll: false, styles: configStyles, filterOption: createFilter({ ignoreAccents: false }), classNamePrefix: "styled-scrollbar react-select" })));
};
var BaseDropdown$1 = React__default.forwardRef(BaseDropdown);

export { BaseDropdown$1 as default };
//# sourceMappingURL=BaseDropdown.js.map
